import React, { useState, useEffect, useMemo } from 'react';
import { ChatTheme, Question } from '@/types/theme';
import { Message } from '@/types/chat';
import { QuestionOptions } from './QuestionOptions';
import { debugLog } from '@/utils/browserLogger';

interface OptionsDisplayProps {
  currentQuestion: Question | null;
  messages: Array<Message>;
  optionsMessageId: string | null;
  isTyping: boolean;
  onOptionSelect?: (value: string) => void;
  theme?: ChatTheme;
}

/**
 * Component to handle displaying question options
 * Extracted to avoid React hooks errors in the MessageList component
 */
export function OptionsDisplay({
  currentQuestion,
  messages,
  optionsMessageId,
  isTyping,
  onOptionSelect,
  theme
}: OptionsDisplayProps) {
  const [showOptions, setShowOptions] = useState(false);

  // Find the message that should show options
  const optionsMessage = messages.find(msg => msg.id === optionsMessageId);

  // Memoize question data to prevent unnecessary re-renders
  const questionForOptions = useMemo(() => {
    if (!optionsMessage) return null;

    const messageMetadata = optionsMessage.metadata || {};
    const questionId = messageMetadata.questionId;
    const questionType = messageMetadata.questionType;
    const questionText = messageMetadata.questionText || optionsMessage.text;
    const questionOptions = messageMetadata.questionOptions || [];

    return {
      id: questionId || optionsMessageId,
      chatbot_id: messageMetadata.chatbot_id || '',
      type: questionType || 'button',
      text: questionText,
      options: questionOptions,
      order: messageMetadata.order || 0,
      shortname: messageMetadata.shortname || '',
      errormessage: messageMetadata.errormessage || '',
      responsevalidation: messageMetadata.responsevalidation || '',
      validation_rules: messageMetadata.validation_rules || {},
      validationType: messageMetadata.validationType || 'none',
      created_at: messageMetadata.created_at || new Date().toISOString(),
      updated_at: messageMetadata.updated_at || new Date().toISOString(),
      placeholder: messageMetadata.placeholder || undefined
    } as Question;
  }, [optionsMessage, optionsMessageId]);

  // Memoize the decision logic to prevent unnecessary re-calculations
  const finalShouldShowOptions = useMemo(() => {
    if (!questionForOptions) return false;

    const isNoResponseType = questionForOptions.type === 'no_response';
    const hasBooleanOptions = questionForOptions.type === 'boolean';
    const hasRegularOptions = questionForOptions.options && questionForOptions.options.length > 0;
    const shouldHaveOptions = hasRegularOptions || hasBooleanOptions;

    return shouldHaveOptions && !isNoResponseType;
  }, [questionForOptions]);

  // Use effect to control options display
  useEffect(() => {
    if (finalShouldShowOptions && !isTyping) {
      setShowOptions(true);
    } else {
      setShowOptions(false);
    }
  }, [finalShouldShowOptions, isTyping]);

  // Early returns for invalid states (after all hooks)
  if (!optionsMessageId || !onOptionSelect || !optionsMessage || !questionForOptions) {
    return null;
  }

  // Render the options if they should be shown

  // Add a wrapper with fade-in animation
  return showOptions ? (
    <div
      style={{
        animation: 'fadeIn 0.5s ease-in-out',
        opacity: 1,
        width: '100%'
      }}
      className="options-wrapper"
      data-question-id={questionForOptions.id}
    >
      <QuestionOptions
        question={questionForOptions}
        onOptionSelect={onOptionSelect}
        theme={theme}
        isDisabled={isTyping}
      />
    </div>
  ) : null;
}
